<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import SettingsAreaIcon from 'dashboard/components-next/icon/SettingsAreaIcon.vue';
import { settingsDestination } from './settingsContext';
import { settingsBackTarget } from './settingsBack';
import { useSettingsCenter } from './useSettingsCenter';

const { t } = useI18n();
const {
  area,
  error,
  funnels,
  groups,
  isAdmin,
  isLoadingFunnels,
  query,
  route,
  router,
  selectedFunnelId,
  loadFunnels,
  invalidFunnelId,
} = useSettingsCenter();

const activeGroup = ref(null);
// O voltar da central leva de onde a pessoa veio e diz o nome do lugar.
const back = computed(() => settingsBackTarget(router, route));
const backLabel = computed(() => {
  if (!back.value?.labelKey) return t('SETTINGS_CENTER.BACK');
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t('SETTINGS_CENTER.BACK_TO', { place: t(back.value.labelKey) });
});
const activeGroupId = computed(() => {
  if (
    activeGroup.value &&
    groups.value.some(group => group.id === activeGroup.value)
  ) {
    return activeGroup.value;
  }
  const preferred = area.value === 'tasks' ? 'tasks' : 'kanban_funnel';
  if (area.value && groups.value.some(group => group.id === preferred))
    return preferred;
  return groups.value[0]?.id || null;
});
const activeItems = computed(() => {
  if (query.value.trim()) return groups.value.flatMap(group => group.items);
  return (
    groups.value.find(group => group.id === activeGroupId.value)?.items || []
  );
});

const scopeLabel = scope => t(`SETTINGS_CENTER.SCOPES.${scope.toUpperCase()}`);
const scopeHelp = scope =>
  t(`SETTINGS_CENTER.SCOPE_HELP.${scope.toUpperCase()}`);
const destination = item =>
  settingsDestination(router, route, {
    ...item,
    query: {
      ...item.query,
      ...(item.scope === 'funnel' && selectedFunnelId.value
        ? { funnel_id: String(selectedFunnelId.value) }
        : {}),
    },
  });
const isFunnelItemBlocked = item =>
  item.scope === 'funnel' &&
  (!selectedFunnelId.value || isLoadingFunnels.value || error.value);
// O card travado so ficava apagado, sem motivo: agora diz o que falta.
const funnelBlockReason = computed(() => {
  if (isLoadingFunnels.value)
    return t('SETTINGS_CENTER.FUNNEL_BLOCKED.LOADING');
  if (error.value) return t('SETTINGS_CENTER.FUNNEL_BLOCKED.ERROR');
  if (!funnels.value.length)
    return t('SETTINGS_CENTER.FUNNEL_BLOCKED.NO_FUNNEL');
  return t('SETTINGS_CENTER.FUNNEL_BLOCKED.CHOOSE');
});
const selectedFunnelName = computed(
  () =>
    funnels.value.find(funnel => funnel.id === selectedFunnelId.value)?.name ||
    ''
);

const goBack = () => {
  if (!back.value) return;
  if (back.value.mode === 'history') router.back();
  else router.push(back.value.to);
};
const showAllSettings = () => {
  const nextQuery = { ...route.query };
  delete nextQuery.config_area;
  activeGroup.value = null;
  router.replace({ query: nextQuery });
};
</script>

<template>
  <main
    class="flex h-full min-h-0 w-full flex-col overflow-hidden bg-n-surface-1 text-n-slate-12"
  >
    <header class="border-b border-n-weak px-4 py-4 sm:px-8">
      <div
        class="mx-auto flex w-full max-w-[1240px] items-start justify-between gap-4"
      >
        <div class="min-w-0">
          <div class="mb-1 flex items-center gap-2 text-xs text-n-slate-10">
            <button
              v-if="back"
              type="button"
              data-testid="settings-center-back"
              class="inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-sm font-semibold text-n-blue-11 hover:bg-n-alpha-2"
              @click="goBack"
            >
              <span
                class="i-ph-arrow-left size-3.5 rtl:rotate-180"
                aria-hidden="true"
              />
              {{ backLabel }}
            </button>
            <span v-if="back" aria-hidden="true">/</span>
            <span>{{ t('SETTINGS_CENTER.CONFIGURE_AREA') }}</span>
          </div>
          <h1 class="truncate text-xl font-semibold sm:text-2xl">
            {{ t('SETTINGS_CENTER.TITLE') }}
          </h1>
          <p class="mt-1 max-w-2xl text-sm text-n-slate-10">
            {{ t('SETTINGS_CENTER.SUBTITLE') }}
          </p>
        </div>
        <span
          class="hidden shrink-0 rounded-full bg-n-alpha-2 px-3 py-1 text-xs text-n-slate-11 sm:inline-flex"
        >
          {{
            isAdmin ? t('SETTINGS_CENTER.ADMIN') : t('SETTINGS_CENTER.MEMBER')
          }}
        </span>
      </div>
    </header>

    <div class="border-b border-n-weak px-4 py-3 sm:px-8">
      <div
        class="mx-auto flex w-full max-w-[1240px] flex-col gap-3 sm:flex-row sm:items-center"
      >
        <label class="relative min-w-0 flex-1">
          <span class="sr-only">{{ t('SETTINGS_CENTER.SEARCH_LABEL') }}</span>
          <input
            v-model="query"
            type="search"
            class="w-full rounded-lg border border-n-weak bg-n-surface-2 px-3 py-2 text-sm outline-none focus:border-n-blue-8"
            :placeholder="t('SETTINGS_CENTER.SEARCH_PLACEHOLDER')"
          />
        </label>
        <label
          v-if="funnels.length"
          class="flex shrink-0 items-center gap-2 text-sm text-n-slate-11"
        >
          <span>{{ t('SETTINGS_CENTER.FUNNEL_LABEL') }}</span>
          <select
            :value="selectedFunnelId"
            class="max-w-[220px] rounded-lg border border-n-weak bg-n-surface-2 px-3 py-2 text-sm outline-none focus:border-n-blue-8"
            @change="
              router.replace({
                query: { ...route.query, funnel_id: $event.target.value },
              })
            "
          >
            <option
              v-for="funnel in funnels"
              :key="funnel.id"
              :value="funnel.id"
            >
              {{ funnel.name }}
            </option>
          </select>
        </label>
      </div>
      <p
        v-if="invalidFunnelId && selectedFunnelName"
        role="status"
        data-testid="settings-center-funnel-not-found"
        class="mx-auto mt-2 mb-0 w-full max-w-[1240px] text-xs text-n-amber-11"
      >
        {{
          t('SETTINGS_CENTER.FUNNEL_NOT_FOUND', { name: selectedFunnelName })
        }}
      </p>
    </div>

    <div
      data-testid="settings-center"
      class="min-h-0 flex-1 overflow-auto px-4 py-5 sm:px-8 sm:py-8"
    >
      <div
        class="mx-auto grid w-full max-w-[1240px] grid-cols-1 gap-8 lg:grid-cols-[220px_minmax(0,1fr)]"
      >
        <nav
          :aria-label="t('SETTINGS_CENTER.SECTIONS')"
          class="flex gap-1 overflow-x-auto lg:block lg:space-y-1"
        >
          <button
            v-if="area"
            type="button"
            class="mb-2 flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 lg:w-full"
            @click="showAllSettings"
          >
            <span class="i-ph-squares-four size-4" aria-hidden="true" />
            {{ t('SETTINGS_CENTER.ALL_SETTINGS') }}
          </button>
          <button
            v-for="group in groups"
            :key="group.id"
            type="button"
            class="flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors lg:w-full"
            :class="
              activeGroupId === group.id
                ? 'bg-n-blue-1 font-medium text-n-blue-11'
                : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
            "
            @click="activeGroup = group.id"
          >
            <span
              class="size-1.5 rounded-full bg-current opacity-60"
              aria-hidden="true"
            />
            {{ group.label }}
            <span class="ml-auto text-xs text-n-slate-9">{{
              group.items.length
            }}</span>
          </button>
        </nav>

        <section class="min-w-0">
          <div
            v-if="isLoadingFunnels"
            class="mb-4 flex items-center gap-2 rounded-lg border border-n-weak bg-n-surface-2 p-4 text-sm text-n-slate-10"
          >
            <span
              class="i-ph-circle-notch animate-spin size-4"
              aria-hidden="true"
            />
            {{ t('SETTINGS_CENTER.LOADING') }}
          </div>
          <div
            v-if="error"
            class="mb-4 flex items-center justify-between gap-4 rounded-lg border border-n-ruby-5 bg-n-ruby-1 p-4 text-sm text-n-ruby-11"
          >
            <div>
              <p class="font-medium">{{ t('SETTINGS_CENTER.ERROR_TITLE') }}</p>
              <p class="mt-1">{{ t('SETTINGS_CENTER.ERROR_DESCRIPTION') }}</p>
            </div>
            <button
              type="button"
              class="shrink-0 rounded-md border border-n-ruby-6 px-3 py-1.5 text-xs font-medium hover:bg-n-ruby-2"
              @click="loadFunnels"
            >
              {{ t('SETTINGS_CENTER.RETRY') }}
            </button>
          </div>
          <div class="mb-5 flex items-end justify-between gap-3">
            <div>
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{
                  query.trim()
                    ? t('SETTINGS_CENTER.SEARCH_RESULTS')
                    : t('SETTINGS_CENTER.CATEGORY')
                }}
              </p>
              <h2 class="mt-1 text-lg font-semibold">
                {{
                  query.trim()
                    ? t('SETTINGS_CENTER.RESULTS_FOR', { query })
                    : groups.find(group => group.id === activeGroupId)?.label ||
                      t('SETTINGS_CENTER.ALL_SETTINGS')
                }}
              </h2>
            </div>
            <span class="text-xs text-n-slate-10">{{
              activeItems.length
            }}</span>
          </div>

          <div
            v-if="activeItems.length"
            class="grid grid-cols-1 gap-4 md:grid-cols-2"
          >
            <component
              :is="isFunnelItemBlocked(item) ? 'button' : RouterLink"
              v-for="item in activeItems"
              :key="item.id"
              :to="isFunnelItemBlocked(item) ? undefined : destination(item)"
              :disabled="isFunnelItemBlocked(item) || undefined"
              :type="isFunnelItemBlocked(item) ? 'button' : undefined"
              :aria-disabled="isFunnelItemBlocked(item) ? 'true' : undefined"
              :tabindex="isFunnelItemBlocked(item) ? -1 : undefined"
              class="flex min-h-[116px] w-full items-start gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4 text-left shadow-sm transition-[border-color,background-color,box-shadow] duration-150 ease-out motion-reduce:transition-none"
              :class="[
                isFunnelItemBlocked(item)
                  ? 'cursor-not-allowed border-dashed'
                  : 'group group/icon-area hover:border-n-blue-7 hover:bg-n-solid-2 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-blue-9 active:bg-n-solid-1 active:shadow-sm',
              ]"
            >
              <span
                class="flex size-9 shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-surface-2 text-n-slate-10 group-hover:border-n-blue-7 group-hover:text-n-blue-11"
              >
                <SettingsAreaIcon :name="item.id" />
              </span>
              <span class="min-w-0 flex-1">
                <span class="flex items-center gap-2">
                  <span class="font-medium">{{ t(item.labelKey) }}</span>
                  <span
                    class="i-ph-arrow-up-right size-3.5 shrink-0 text-n-slate-9 group-hover:text-n-blue-11"
                    aria-hidden="true"
                  />
                </span>
                <span class="mt-1 block text-xs leading-5 text-n-slate-10">{{
                  t(item.descriptionKey)
                }}</span>
                <span
                  v-if="isFunnelItemBlocked(item)"
                  data-testid="settings-center-blocked-reason"
                  class="mt-2 flex items-center gap-1.5 text-xs font-medium text-n-amber-11"
                >
                  <span
                    class="i-ph-info size-3.5 shrink-0"
                    aria-hidden="true"
                  />
                  {{ funnelBlockReason }}
                </span>
                <span
                  class="mt-2 inline-flex items-center rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] text-n-slate-10"
                >
                  {{ scopeLabel(item.scope) }}
                </span>
              </span>
            </component>
          </div>
          <div
            v-else
            class="rounded-lg border border-dashed border-n-weak p-8 text-center"
          >
            <span
              class="i-ph-magnifying-glass mb-3 inline-block size-6 text-n-slate-9"
              aria-hidden="true"
            />
            <p class="text-sm font-medium">
              {{ t('SETTINGS_CENTER.EMPTY_TITLE') }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('SETTINGS_CENTER.EMPTY_DESCRIPTION') }}
            </p>
          </div>
          <p class="mt-6 max-w-2xl text-xs leading-5 text-n-slate-10">
            {{ scopeHelp(activeItems[0]?.scope || 'account') }}
          </p>
        </section>
      </div>
    </div>
  </main>
</template>
